import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Loader2, FileText, Trash2, ExternalLink, Pencil, Sparkles, Clock, CheckCircle2, FileEdit,
} from 'lucide-react';
import { api } from '../lib/api';
import { cn } from '../lib/cn';
import { BlogPostModal } from '../components/BlogPostModal';

type Status = 'draft' | 'scheduled' | 'published';

type PostRow = {
  id: string;
  slug: string;
  title: string;
  status: Status;
  source: 'manual' | 'auto';
  category: string;
  featuredImageUrl: string | null;
  scheduledAt: string | null;
  publishedAt: string | null;
  updatedAt: string;
};

const STATUS_META: Record<Status, { label: string; cls: string; Icon: typeof Clock }> = {
  draft:     { label: 'Borrador',   cls: 'bg-slate-100 text-slate-600',   Icon: FileEdit },
  scheduled: { label: 'Programado', cls: 'bg-amber-100 text-amber-700',   Icon: Clock },
  published: { label: 'Publicado',  cls: 'bg-emerald-100 text-emerald-700', Icon: CheckCircle2 },
};

const fmtDate = (d: string | null) =>
  d ? new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export function AdminBlog() {
  const qc = useQueryClient();
  const [modal, setModal] = useState<{ open: boolean; postId: string | null }>({ open: false, postId: null });

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'blog'],
    queryFn: () => api<{ posts: PostRow[] }>('/api/admin/blog'),
  });
  const posts = data?.posts ?? [];

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api(`/api/admin/blog/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'blog'] }),
  });

  function refresh() {
    void qc.invalidateQueries({ queryKey: ['admin', 'blog'] });
  }

  return (
    <div className="space-y-6">
      {/* header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileText size={22} className="text-brand-600" /> Blog
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {isLoading ? '…' : `${posts.length} artículo${posts.length === 1 ? '' : 's'}`}
          </p>
        </div>
        <button
          onClick={() => setModal({ open: true, postId: null })}
          className="btn-primary gap-2"
        >
          <Plus size={16} /> Nuevo artículo
        </button>
      </div>

      {/* list */}
      {isLoading ? (
        <div className="p-12 flex items-center justify-center gap-2 text-slate-400 text-sm">
          <Loader2 size={16} className="animate-spin" /> Cargando artículos…
        </div>
      ) : posts.length === 0 ? (
        <div className="p-12 flex flex-col items-center justify-center gap-3 text-slate-400 border border-dashed border-slate-200 rounded-2xl">
          <FileText size={28} />
          <p className="text-sm">Todavía no hay artículos. Crea el primero.</p>
          <button onClick={() => setModal({ open: true, postId: null })} className="btn-primary gap-2">
            <Plus size={16} /> Nuevo artículo
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {posts.map((post) => {
            const sm = STATUS_META[post.status];
            return (
              <div
                key={post.id}
                className="group bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-4 hover:border-brand-300 hover:shadow-sm transition"
              >
                <div className="h-14 w-20 rounded-lg bg-slate-100 overflow-hidden shrink-0">
                  {post.featuredImageUrl && (
                    <img src={post.featuredImageUrl} alt="" className="h-full w-full object-cover" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={cn('inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-2 py-0.5', sm.cls)}>
                      <sm.Icon size={11} /> {sm.label}
                    </span>
                    {post.source === 'auto' && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-2 py-0.5 bg-brand-100 text-brand-700">
                        <Sparkles size={11} /> Autoblog
                      </span>
                    )}
                    {post.category && (
                      <span className="text-[11px] text-slate-400">{post.category}</span>
                    )}
                  </div>
                  <p className="mt-1 font-semibold text-slate-800 truncate">{post.title}</p>
                  <p className="text-xs text-slate-400">
                    {post.status === 'scheduled'
                      ? `Programado para ${fmtDate(post.scheduledAt)}`
                      : post.status === 'published'
                      ? `Publicado el ${fmtDate(post.publishedAt)}`
                      : `Editado el ${fmtDate(post.updatedAt)}`}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {post.status === 'published' && (
                    <a
                      href={`/blog/${post.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      title="Ver publicado"
                      className="h-9 w-9 inline-flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    >
                      <ExternalLink size={15} />
                    </a>
                  )}
                  <button
                    onClick={() => setModal({ open: true, postId: post.id })}
                    title="Editar"
                    className="h-9 w-9 inline-flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-brand-600"
                  >
                    <Pencil size={15} />
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(`¿Eliminar "${post.title}"? Esta acción no se puede deshacer.`)) {
                        deleteMutation.mutate(post.id);
                      }
                    }}
                    title="Eliminar"
                    className="h-9 w-9 inline-flex items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <BlogPostModal
        open={modal.open}
        postId={modal.postId}
        onClose={() => setModal({ open: false, postId: null })}
        onSaved={refresh}
      />
    </div>
  );
}
